import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2, Search, Map, TrendingUp, ShieldCheck, CalendarClock, PhoneCall } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

// Optional external scheduling link (Calendly etc). Leave empty to show the
// "we'll reach out within 24h" message instead.
const BOOKING_URL = "";

const deliverables = [
  { icon: Search, title: "Workflow analysis", desc: "We map the manual work eating your team's hours — support, data entry, follow-ups, scheduling." },
  { icon: Map, title: "Automation roadmap", desc: "Exactly which AI agent or app replaces that work, and how it plugs into your existing tools." },
  { icon: TrendingUp, title: "ROI estimate", desc: "Hours saved per week and what that's worth — so you can decide with real numbers." },
  { icon: ShieldCheck, title: "Zero obligation", desc: "No pitch deck, no pressure. You keep the roadmap whether or not you build with us." },
];

export default function AuditSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", workflow: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.workflow) {
      toast.error("Please fill in your name, email and the workflow you'd like automated.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/audit-request", form);
      toast.success(data.message || "Audit request received!");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="audit" className="py-24 relative border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 grid-bg radial-fade opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-start">
        {/* Left — the offer */}
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-neon-yellow mb-3">// free · 15 minutes · no strings</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Free 15-Minute <br />
            <span className="text-neon-cyan text-glow-cyan">AI Integration Audit</span>
          </h2>
          <p className="mt-5 text-white/55 max-w-lg leading-relaxed">
            Not sure where AI fits in your business? Tell us about one manual workflow — customer
            service, data entry, scheduling, follow-ups — and in a short call we'll show you exactly
            how an AI agent can take it off your team's plate.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {deliverables.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                className="rounded-xl border border-white/10 bg-surface/60 p-5"
              >
                <d.icon size={20} className="text-neon-cyan" />
                <h3 className="mt-3 font-semibold">{d.title}</h3>
                <p className="mt-1.5 text-sm text-white/50 leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right — the form / thank-you */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-neon-cyan/20 glow-border bg-surface p-6 sm:p-8"
        >
          {done ? (
            <div data-testid="audit-success" className="text-center py-10">
              <CheckCircle2 size={48} className="text-neon-cyan mx-auto" />
              <h3 className="mt-5 font-heading text-2xl font-black tracking-tight">You're booked in.</h3>
              <p className="mt-3 text-white/60 leading-relaxed max-w-sm mx-auto">
                We've got your details. {BOOKING_URL ? "Pick a time that works for you below — or we'll reach out within 24 hours." : "We'll reach out within 24 hours to schedule your 15-minute audit call."}
              </p>
              {BOOKING_URL ? (
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="audit-booking-link"
                  className="neon-btn inline-flex items-center gap-2 mt-7 bg-neon-cyan text-black font-semibold px-7 py-3.5 rounded-full"
                >
                  <CalendarClock size={18} /> Pick your 15-min slot
                </a>
              ) : (
                <a
                  href="tel:+19047891050"
                  data-testid="audit-call-now"
                  className="neon-btn inline-flex items-center gap-2 mt-7 bg-neon-cyan text-black font-semibold px-7 py-3.5 rounded-full"
                >
                  <PhoneCall size={18} /> Can't wait? Call now
                </a>
              )}
            </div>
          ) : (
            <form onSubmit={submit} data-testid="audit-form" className="space-y-4">
              <h3 className="font-heading text-xl font-bold tracking-tight">Claim your free audit</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Name *</Label>
                  <Input data-testid="audit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-void border-white/15 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Email *</Label>
                  <Input data-testid="audit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-void border-white/15 mt-1" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Phone</Label>
                  <Input data-testid="audit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-void border-white/15 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Company</Label>
                  <Input data-testid="audit-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-void border-white/15 mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-white/60">What manual work eats your team's time? *</Label>
                <Textarea
                  data-testid="audit-workflow"
                  value={form.workflow}
                  onChange={(e) => setForm({ ...form, workflow: e.target.value })}
                  className="bg-void border-white/15 mt-1"
                  rows={4}
                  placeholder="e.g. We answer the same 30 customer emails every day / we re-type orders into a spreadsheet…"
                />
              </div>
              <button
                type="submit"
                data-testid="audit-submit"
                disabled={submitting}
                className="neon-btn w-full bg-neon-cyan text-black font-semibold py-3.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                Get my free audit
              </button>
              <p className="text-[11px] text-white/35 text-center font-mono">15 minutes · zero obligation · keep the roadmap either way</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
